import { Component, ElementRef, HostListener, ChangeDetectorRef } from '@angular/core';

import { ComponentsService } from '../../services/components.service';
import { CustomFontUrlValidationService } from '../services/custom-font-url-validation.service';
import { CustomFontListManagementService } from '../services/custom-font-list-management.service';
import { CurrentPlanService } from 'src/app/components/plans/current-plan.service';
import { StorageManagerService } from 'src/app/storage/services/storage-manager.service';
import { StorageUtilsService } from 'src/app/storage/services/storage-utils.service';

interface UploadManager {
  uploadedFiles: any[];
  folderPath: string;
  isSingleFileSelector(): boolean;
  onUploadStatus(isUploading: boolean): void;
  addFile(file: any): void;
}

@Component({
  selector: 'component-custom-font-selector',
  templateUrl: './custom-font-selector.component.html',
  styleUrls: ['./custom-font-selector.component.scss'],
  standalone: false
})

export class CustomFontSelectorComponent {
  fontUrl: string;
  validationResult: string;
  spinner = false;
  /** Currently selected Storage file-type – limited to values of StorageManagerService.FILE_TYPE */
  fileType: keyof typeof StorageManagerService.FILE_TYPE;
  /** Manager injected into `<basic-uploader>` */
  storageUploadManager: UploadManager;
  isUploading = false;

  constructor(private elementRef: ElementRef,
    private componentsService: ComponentsService,
    private changeDetectorRef: ChangeDetectorRef,
    private customFontUrlValidationService: CustomFontUrlValidationService,
    private customFontListManagementService: CustomFontListManagementService,
    public currentPlanService: CurrentPlanService,
    private storageUtilsService: StorageUtilsService,
    private storageManagerService: StorageManagerService
  ) {
    componentsService.registerDirective({
      type: 'rise-custom-font-selector',
      element: this.elementRef.nativeElement,
      show: () => {
        this._init();
        this.fontUrlChanged();
      }
    });

    this.storageUploadManager = {
      uploadedFiles: [],
      folderPath: '',
      isSingleFileSelector: () => {
        return true;
      },
      onUploadStatus: (isUploading: boolean) => {
        const wasUploading = this.isUploading;
        this.isUploading = isUploading;

        if (wasUploading && isUploading === false) {
          const selectedFile = this.storageUploadManager.uploadedFiles[0];

          if (selectedFile) {
            this.saveStorageFont(selectedFile).catch(console.error);
          }

          this.storageUploadManager.uploadedFiles = [];
        }
      },
      addFile: (file) => {
        this.storageUploadManager.uploadedFiles.push(file);
      }
    };
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange() {
    if (this._directiveIsVisible() && !document.hidden) {
      this.fontUrlChanged();
    }
  }

  _init() {
    this.fileType = StorageManagerService.FILE_TYPE.FONT as keyof typeof StorageManagerService.FILE_TYPE;
    this.fontUrl = '';

    this.storageManagerService.isSingleFileSelector = () => {
      return true;
    };

    this.storageManagerService.onSelectHandler = (selectedItems) => {
      const selectedFile = selectedItems[0];

      if (selectedFile) {
        this.saveStorageFont(selectedFile);
      }
    };
  }

  _directiveIsVisible() {
    // This directive is instantiated once by templateAttributeEditor
    // It becomes visible when <rise-custom-font-selector> is selected
    return this.componentsService.selected && (this.componentsService.selected.type === 'rise-custom-font-selector');
  }

  _isValidUrl(url: string): boolean {
    // If no protocol specified, prepend https://
    if (!url.includes('://')) {
      url = 'https://' + url;
    }

    let normalizedUrl: URL;
    try {
      normalizedUrl = new URL(url);
    } catch (error) {
      console.log(error);
      return false;
    }

    // Ensure protocol is https
    return normalizedUrl.protocol === "https:";
  }

  async _validateFontUrl() {
    let _fontUrl = !this.fontUrl ? '' : this.fontUrl.trim();
    if (_fontUrl === '') {
      //empty string is allowed
      return 'VALID';
    }

    //check if url is valid and secure
    if (this._isValidUrl(_fontUrl)) {
      return await this._validateFontUrlRemotely();
    }

    return 'INVALID_URL';
  }

  async _validateFontUrlRemotely() {
    this.spinner = true;

    try {
      return await this.customFontUrlValidationService.validate(this.fontUrl);
    } catch (error) {
      console.error('Error validating font URL:', error);
      return 'LOAD_FAILURE';
    } finally {
      this.spinner = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  async saveStorageFont(file: any) {
    if (!file || !this.storageUtilsService.fileIsFont(file)) {
      return;
    }

    const filePath = this.storageUtilsService.getFilePath(file);
    const fileName = this.storageUtilsService.fileName(file);

    this.spinner = true;

    try {
      await this.customFontListManagementService.addFont(filePath, fileName);
      this.customFontListManagementService.selectFont(filePath);
      this.componentsService.showPreviousPage();
    } catch (error) {
      console.error('Failed to save custom font from storage');
    } finally {
      this.spinner = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  async saveFontUrl() {
    if (this.validationResult !== 'VALID' || !this.fontUrl) {
      return;
    }

    this.spinner = true;
    try {
      await this.customFontListManagementService.addFont(this.fontUrl);
      this.customFontListManagementService.selectFont(this.fontUrl);
      this.componentsService.showPreviousPage();
    } catch (error) {
      console.error('Failed to save custom font:', error);
    } finally {
      this.spinner = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  clearValidationResult() {
    this.validationResult = '';
  }

  async fontUrlChanged() {
    this.clearValidationResult();

    this.validationResult = await this._validateFontUrl();
    this.changeDetectorRef.detectChanges();
  }

  activateUploader() {
    const uploader = document.getElementById('custom-font-uploader') as HTMLInputElement;
    if (uploader) {
      uploader.click();
    }
  }

  selectFromStorage() {
    this.storageManagerService.fileType = StorageManagerService.FILE_TYPE.FONT;

    this.componentsService.editComponent({
      type: 'rise-storage-selector'
    });
  }
}